import { createAsyncThunk } from '@reduxjs/toolkit';
import FileServiceResource from 'api/service/FileResource';
import FilesServiceResource from 'api/service/FilesResource';
import QuoteSetResource from 'api/service/QuoteSetResource';
import { blobDownload } from 'utils/download';

export const createFolder = createAsyncThunk('files/createFolder', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { form, parent } = initialData;
  const response = await filesResource.createFolder({
    name: form.name,
    ...(parent && { parent }),
  });
  if (response.status === 201) {
    return response.data;
  }
  return rejectWithValue('Folder creation failed.');
});

export const renameFile = createAsyncThunk('files/renameFile', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { id, name } = initialData;
  const response = await filesResource.file(id).rename(name);
  if (response.status === 200) {
    return response.data;
  }
  return rejectWithValue('File not found');
});

export const deleteFile = createAsyncThunk('files/deleteFile', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const id = initialData;
  const response = await filesResource.file(id).delete();
  if (response.status === 202) {
    return id;
  }
  return rejectWithValue('Files not found');
});

export const deleteQuoteSet = createAsyncThunk('files/deleteQuoteSet', async (initialData, { rejectWithValue }) => {
  const quoteSetResource = new QuoteSetResource();
  const id = initialData;
  const { status } = await quoteSetResource.delete(id);
  if (status === 202) {
    return id;
  }
  return rejectWithValue('Files not found');
});

export const updateQuoteSet = createAsyncThunk(
  'marketplace/updateQuoteSet',
  async (initialData, { rejectWithValue }) => {
    const quoteSetResource = new QuoteSetResource();
    const { id, name } = initialData;
    const { status, data } = await quoteSetResource.update(id, name);
    if (status === 200) {
      return data;
    }
    return rejectWithValue('Quote not found');
  }
);

export const downloadFileData = createAsyncThunk('files/downloadFileData', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { id } = initialData;
  const { status, data } = await filesResource.file(id).download();
  if (status === 200) {
    const objectUrl = URL.createObjectURL(data);
    return objectUrl;
  }
  return rejectWithValue('File download failed');
});

export const downloadFile = createAsyncThunk('files/downloadFile', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { id, name } = initialData;
  const response = await filesResource.file(id).download();
  if (response.status === 200) {
    blobDownload(response.data, name);
    return;
  }
  return rejectWithValue('File download failed');
});

export const duplicateFile = createAsyncThunk('files/duplicateFile', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { oid, format, parent } = initialData;
  const response = await filesResource.file(oid).duplicate(parent);
  if (response.status === 200) {
    return response.data;
  }
  return rejectWithValue(`${format} duplication failed`);
});

export const shareFile = createAsyncThunk('files/shareFile', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { fileId, userId, access } = initialData;
  const response = await filesResource.fileShare(fileId).share(userId, access);
  if (response.status === 200) {
    return response.data;
  }
  return rejectWithValue('File not found');
});

export const shareFileUpdateAccess = createAsyncThunk(
  'files/shareFileUpdateAccess',
  async (initialData, { rejectWithValue }) => {
    const filesResource = new FilesServiceResource();
    const { fileId, userId, access } = initialData;
    const response = await filesResource.fileShare(fileId).updateAccess(userId, access);
    if (response.status === 200) {
      return response.data;
    }
    return rejectWithValue('File not found');
  }
);

export const shareList = createAsyncThunk('files/shareList', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { fileId } = initialData;
  const { status, data } = await filesResource.fileShare(fileId).list();
  if (status === 200) {
    return data;
  }
  return rejectWithValue('File not found');
});

export const shareFileRemove = createAsyncThunk('files/shareFileRemove', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { fileId, userId } = initialData;
  const { status } = await filesResource.fileShare(fileId).unshare(userId);
  if (status === 202) {
    return true;
  }
  return rejectWithValue('File not found');
});

export const getFilePreview = createAsyncThunk('files/getFilePreview', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { id } = initialData;
  const { data, status } = await filesResource.download(id);
  if (status === 200) {
    // convert response data blob to image url
    const imageUrl = URL.createObjectURL(data);
    return imageUrl;
  }
  return rejectWithValue('File preview photo not found.');
});

export const refreshFile = createAsyncThunk('files/refreshFile', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { id } = initialData;
  const { status, data } = await filesResource.file(id).refresh();
  if (status === 200) {
    return data;
  }
  return rejectWithValue('File refresh failed');
});

export const getFileUriSas = createAsyncThunk('files/getFileUriSas', async (initialData, { rejectWithValue }) => {
  const { id } = initialData;
  const fileServiceResource = new FilesServiceResource();
  const { status, data } = await fileServiceResource.file(id).getUriSas();
  if (status === 200) {
    return data;
  }
  return rejectWithValue('File refresh failed');
});

export const getExportedFile = createAsyncThunk('files/getExportedFile', async (initialData, { rejectWithValue }) => {
  const { id, params } = initialData;
  const fileServiceResource = new FileServiceResource();
  const { status, data } = await fileServiceResource.read(id);

  if (status === 200) {
    if (data?.export_files?.pdf) return data.export_files.pdf;
    const { status: exportStatus, data: exportData } = await fileServiceResource.export(id, params);

    if (exportStatus === 200) return exportData?.export_files?.pdf;
    return rejectWithValue('File export failed');
  }
  return rejectWithValue('File get failed');
});

export const getFile = createAsyncThunk('files/getFile', async (initialData, { rejectWithValue }) => {
  const { id } = initialData;

  const fileResource = new FileServiceResource();
  const { status, data } = await fileResource.read(id);
  if (status === 200) {
    return data;
  }
  return rejectWithValue('File get failed');
});

export const moveFile = createAsyncThunk('files/moveFile', async (initialData, { rejectWithValue }) => {
  const filesResource = new FilesServiceResource();
  const { id, new_parent } = initialData;
  const params = { ...(new_parent && { new_parent: new_parent }), includes: 'parent' };
  const { status, data } = await filesResource.file(id).move(params);
  if (status === 200) {
    return data;
  }
  return rejectWithValue('File move failed');
});
